/**
 * Intraction Custom Translation Hook
 *
 * This hook wraps react-i18next's useTranslation to provide custom functionality:
 * 1. Automatically strips $PLEASE_TRANSLATE$ markers from translations
 * 2. Provides a consistent API across the application
 * 3. Easy to extend with additional custom behavior in the future
 *
 * @example Basic usage
 * ```tsx
 * import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
 *
 * function MyComponent() {
 *   const { t } = useTranslation()
 *   return <h1>{t('welcome.title')}</h1>
 * }
 * ```
 *
 * @example With interpolation
 * ```tsx
 * const { t } = useTranslation()
 *
 * // Translation: "Hello {{name}}, you have {{count}} messages"
 * t('welcome.message', { name: 'John', count: 5 })
 * // Output: "Hello John, you have 5 messages"
 * ```
 *
 * @example With Trans component for complex HTML
 * ```tsx
 * import { useTranslation, Trans } from '@/shared/hooks/useIntractionTranslate'
 *
 * function MyComponent() {
 *   const { t } = useTranslation()
 *
 *   return (
 *     <Trans i18nKey="userMessages" count={5}>
 *       Hello <strong>{{name}}</strong>, you have {{count}} messages.
 *     </Trans>
 *   )
 * }
 * ```
 */

import { useState, useEffect } from 'react'
import { useTranslation as useReactI18nTranslation, Trans as ReactI18nTrans } from 'react-i18next'
import { processTranslationValue } from '../utils/translationHelpers'
import { loadDevModeFromStorage } from '../libs/devModePersistence'
import i18n from '../../i18n'

/**
 * Custom translation hook that extends react-i18next with Intraction-specific functionality
 *
 * Features:
 * - Automatic $PLEASE_TRANSLATE$ marker stripping
 * - Escape sequence processing (\n, \t, \\, \r)
 * - Full interpolation support: t('key', { var1: value1, var2: value2 })
 * - Pluralization support: t('key', { count: 5 })
 * - Context support: t('key', { context: 'male' })
 * - All standard i18next features
 *
 * @param ns - Optional namespace(s) to use
 * @param options - Optional translation options
 * @returns Enhanced translation response with automatic marker stripping
 */
export function useTranslation(
  ...args: Parameters<typeof useReactI18nTranslation>
): ReturnType<typeof useReactI18nTranslation> {
  const [ns, options] = args
  const translation = useReactI18nTranslation(ns, options)

  // Track i18n-show-keys state reactively so components re-render when it changes
  const [showI18nKeys, setShowI18nKeys] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem('i18n-show-keys') === 'true'
    } catch {
      return false
    }
  })

  // Track debug mode state reactively
  const [isDebugModeEnabled, setIsDebugModeEnabled] = useState(() => loadDevModeFromStorage())

  // Listen for localStorage changes to update state and trigger re-renders
  useEffect(() => {
    if (typeof window === 'undefined') return

    const checkStates = () => {
      try {
        const newShowI18nKeys = localStorage.getItem('i18n-show-keys') === 'true'
        const newDebugMode = loadDevModeFromStorage()

        let shouldUpdate = false
        if (newShowI18nKeys !== showI18nKeys) {
          setShowI18nKeys(newShowI18nKeys)
          shouldUpdate = true
        }
        if (newDebugMode !== isDebugModeEnabled) {
          setIsDebugModeEnabled(newDebugMode)
          shouldUpdate = true
        }

        // Trigger i18next re-render if state changed
        if (shouldUpdate) {
          i18n.emit('languageChanged', i18n.language)
        }
      } catch {
        // Ignore errors
      }
    }

    // Check on mount and periodically (for same-tab and cross-tab updates)
    checkStates()
    const interval = setInterval(checkStates, 200) // Check every 200ms for responsiveness

    // Listen for storage events (cross-tab updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'i18n-show-keys' || e.key === 'interax_dev_mode_enabled') {
        checkStates()
      }
    }

    // Listen for custom event (same-tab updates)
    const handleI18nKeysToggle = () => {
      checkStates()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('i18n-keys-toggled', handleI18nKeysToggle)

    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('i18n-keys-toggled', handleI18nKeysToggle)
    }
  }, [showI18nKeys, isDebugModeEnabled])

  // Wrap the t function to automatically strip $PLEASE_TRANSLATE$ markers
  const originalT = translation.t

  const enhancedT = ((
    key: string | string[],
    defaultValueOrOptions?: string | Record<string, unknown>,
    options?: Record<string, unknown>
  ) => {
    // i18next's own `t(key, defaultValue, options)` form — flattening it to two arguments dropped the interpolation values.
    const opts =
      typeof defaultValueOrOptions === 'string'
        ? { defaultValue: defaultValueOrOptions, ...(options ?? {}) }
        : defaultValueOrOptions
    try {
      const result = (originalT as (k: string | string[], o?: Record<string, unknown>) => string)(key, opts)

      // Handle case where returnObjects: true was requested but i18next returned a JSON string
      // This can happen if the translation source stored the array as a JSON string
      let processed: string | unknown[] = result
      if (opts?.returnObjects && typeof result === 'string' && result.trim().startsWith('[')) {
        try {
          const parsed: unknown = JSON.parse(result)
          if (Array.isArray(parsed)) {
            processed = parsed
          }
        } catch (parseError) {
          // If parsing fails, treat as regular string (might be intentional string value)
          // No need to log - this is expected behavior for non-JSON strings
        }
      }

      // Process the result recursively (handles strings, arrays, objects)
      processed = processTranslationValue(processed) as typeof processed

      // Show the key if both debug mode and i18n-show-keys toggle are enabled
      // This works on both localhost and deployed website
      // Note: Debug key display only applies to top-level strings, not array elements
      if (typeof window !== 'undefined' && typeof processed === 'string') {
        const hasQueryParam = new URLSearchParams(window.location.search).has('i18n-keys')

        if ((isDebugModeEnabled && showI18nKeys) || hasQueryParam) {
          // Show key in a subtle format: "Translation Text [key: namespace.key.path]"
          const keyString = typeof key === 'string' ? key : String(key)

          return `${processed} [key: ${keyString}]`
        }
      }

      return processed
    } catch (error) {
      // Fallback to key if translation fails
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[useTranslation] Translation error for key "${key}":`, error)
      }
      return typeof key === 'string' ? key : ''
    }
  }) as unknown as typeof originalT

  return {
    ...translation,
    t: enhancedT
  }
}

/**
 * Trans component for complex translations with React elements
 *
 * Use this when you need to include React components (like links, bold text, etc.)
 * within your translated strings. Automatically works with our custom hook.
 *
 * @example
 * ```tsx
 * <Trans i18nKey="welcomeMessage">
 *   Hello <strong>{{name}}</strong>, click <Link to="/profile">here</Link>.
 * </Trans>
 * ```
 *
 * Translation: "Hello <1>{{name}}</1>, click <3>here</3>."
 */
export const Trans = ReactI18nTrans

// Re-export other i18n utilities for convenience
export { useTranslationWithMarkerStrip } from '../utils/translationHelpers'
export type { UseTranslationOptions, UseTranslationResponse } from 'react-i18next'
